/**
 * AdMob service — Capacitor wrapper around @capacitor-community/admob.
 *
 * Strategy:
 *  • Web (Chrome / production website) → all calls are no-ops. AdSense (the
 *    web-only ad provider) keeps showing on the website unchanged.
 *  • Native Android (Capacitor AAB) → initializes Google Mobile Ads SDK,
 *    shows a bottom banner once at startup, and offers `showInterstitial()`
 *    to be called at meaningful moments (end of a breathing/body-scan/yoga
 *    session) without breaking the user's flow.
 *
 * IDs come from REACT_APP_ADMOB_* env variables so we can swap test/prod
 * without recompiling React. They default to Google's official test units
 * when not set, so the integration is always safe to ship.
 */
import { Capacitor } from '@capacitor/core';

const IS_IOS = (() => {
  try { return Capacitor.getPlatform() === 'ios'; } catch { return false; }
})();

// iOS uses its own AdMob app + ad units (created in AdMob for the App Store version)
const IOS_INTERSTITIAL_ID =
  process.env.REACT_APP_ADMOB_IOS_INTERSTITIAL_ID ||
  // Google test interstitial unit (iOS)
  'ca-app-pub-3940256099942544/4411468910';

const IOS_BANNER_ID = process.env.REACT_APP_ADMOB_IOS_BANNER_ID || null;

// iOS App Open ad unit ("annonce à l'ouverture") — shown at launch/resume on iOS only
const IOS_APPOPEN_ID =
  process.env.REACT_APP_ADMOB_IOS_APPOPEN_ID ||
  // Google test App Open unit (iOS)
  'ca-app-pub-3940256099942544/5575463023';

const ADMOB_BANNER_ID = IS_IOS
  ? IOS_BANNER_ID
  : (process.env.REACT_APP_ADMOB_BANNER_ID ||
    // Google test banner unit — always returns a fake ad in dev
    'ca-app-pub-3940256099942544/6300978111');

const ADMOB_INTERSTITIAL_ID = IS_IOS
  ? IOS_INTERSTITIAL_ID
  : (process.env.REACT_APP_ADMOB_INTERSTITIAL_ID ||
    // Google test interstitial unit
    'ca-app-pub-3940256099942544/1033173712');

// Toggle: when REACT_APP_ADMOB_TESTING === 'false' we ship real ads.
const TESTING_MODE =
  (process.env.REACT_APP_ADMOB_TESTING || 'true').toLowerCase() === 'true';

// Banner is only shown if a unit exists for the current platform.
// iOS: no banner unit configured yet → no banner on iOS.
export const BANNER_ENABLED = !!ADMOB_BANNER_ID && (TESTING_MODE ||
  (IS_IOS ? !!process.env.REACT_APP_ADMOB_IOS_BANNER_ID : !!process.env.REACT_APP_ADMOB_BANNER_ID));

let initialized = false;
let bannerShown = false;
let interstitialReady = false;

const isNative = () =>
  typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform?.();

/** Lazy-import the plugin so the web bundle doesn't try to resolve it. */
async function loadAdMob() {
  if (!isNative()) return null;
  const mod = await import('@capacitor-community/admob');
  return mod;
}

export async function initAdMob() {
  if (initialized || !isNative()) return;
  try {
    const m = await loadAdMob();
    if (!m) return;
    await m.AdMob.initialize({
      requestTrackingAuthorization: true,
      testingDevices: [],
      initializeForTesting: TESTING_MODE,
    });
    initialized = true;
    // eslint-disable-next-line no-console
    console.log('[AdMob] initialized (testing =', TESTING_MODE, ')');
  } catch (e) {
    console.warn('[AdMob] init failed', e);
  }
}

export async function showBottomBanner() {
  if (!isNative() || bannerShown || !BANNER_ENABLED) return;
  try {
    const m = await loadAdMob();
    if (!m) return;
    await m.AdMob.showBanner({
      adId: ADMOB_BANNER_ID,
      adSize: m.BannerAdSize.ADAPTIVE_BANNER,
      position: m.BannerAdPosition.BOTTOM_CENTER,
      margin: 0,
      isTesting: TESTING_MODE,
    });
    bannerShown = true;
    // Reserve space at the bottom so the native banner never covers buttons
    document.body.classList.add('has-admob-banner');
  } catch (e) {
    console.warn('[AdMob] showBanner failed', e);
  }
}

export async function hideBanner() {
  if (!isNative() || !bannerShown) return;
  try {
    const m = await loadAdMob();
    if (!m) return;
    await m.AdMob.hideBanner();
    bannerShown = false;
    document.body.classList.remove('has-admob-banner');
  } catch (e) {
    console.warn('[AdMob] hideBanner failed', e);
  }
}

/** Pre-loads an interstitial in the background so it's ready when needed. */
export async function prepareInterstitial() {
  if (!isNative() || interstitialReady) return;
  try {
    const m = await loadAdMob();
    if (!m) return;
    await m.AdMob.prepareInterstitial({
      adId: ADMOB_INTERSTITIAL_ID,
      isTesting: TESTING_MODE,
    });
    interstitialReady = true;
  } catch (e) {
    console.warn('[AdMob] prepareInterstitial failed', e);
    interstitialReady = false;
  }
}

/**
 * Show the pre-loaded interstitial. Call at the END of a meaningful action
 * (session finished, modal closed) — never during a relaxation exercise.
 */
export async function showInterstitial() {
  if (!isNative()) return;
  try {
    const m = await loadAdMob();
    if (!m) return;
    if (!interstitialReady) {
      await m.AdMob.prepareInterstitial({
        adId: ADMOB_INTERSTITIAL_ID,
        isTesting: TESTING_MODE,
      });
    }
    await m.AdMob.showInterstitial();
    interstitialReady = false;
    // Preload the next one
    prepareInterstitial();
  } catch (e) {
    console.warn('[AdMob] showInterstitial failed', e);
  }
}

/* ------------------------------------------------------------------ */
/* App Open ads (iOS only)                                             */
/* ------------------------------------------------------------------ */
let appOpenLastShown = 0;
const APPOPEN_COOLDOWN_MS = 5 * 60 * 1000; // never more than once / 5 min

async function loadAndShowAppOpen(m) {
  const now = Date.now();
  if (now - appOpenLastShown < APPOPEN_COOLDOWN_MS) return;
  await m.AdMob.loadAppOpen({ adId: IOS_APPOPEN_ID, isTesting: TESTING_MODE });
  const { value } = await m.AdMob.isAppOpenLoaded({ adId: IOS_APPOPEN_ID });
  if (value) {
    await m.AdMob.showAppOpen({ adId: IOS_APPOPEN_ID });
    appOpenLastShown = Date.now();
  }
}

/**
 * iOS only: shows an App Open ad shortly after launch, and again when the
 * app returns to the foreground (with a 5-minute cooldown). No-op on
 * Android and on the web.
 */
export async function initAppOpenAds() {
  if (!isNative() || !IS_IOS) return;
  try {
    const m = await loadAdMob();
    if (!m) return;
    // Launch: wait for the splash to finish before covering the screen
    setTimeout(() => {
      loadAndShowAppOpen(m).catch((e) =>
        console.warn('[AdMob] appOpen launch failed', e)
      );
    }, 3500);
    // Foreground resume
    try {
      const { App } = await import('@capacitor/app');
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          loadAndShowAppOpen(m).catch((e) =>
            console.warn('[AdMob] appOpen resume failed', e)
          );
        }
      });
    } catch (_) {}
  } catch (e) {
    console.warn('[AdMob] initAppOpenAds failed', e);
  }
}

export const AdMobAvailable = isNative;
